import { baseKeymap, toggleMark, chainCommands, exitCode } from "prosemirror-commands";
import { undo, redo, history } from "prosemirror-history";
import { keymap } from "prosemirror-keymap";
import { Plugin } from "prosemirror-state";

import { addLink } from "./common";
import getMenu from "./menu";

const WORD_REGEX = /\w+/g;

function getWordCount(text) {
    const match = text.match(WORD_REGEX);
    return match ? match.length : 0;
}

function getWordCountText(wordCount, min, max) {
    const pieces = ["Words: "];

    if (min) {
        pieces.push(`<span class="comment--item-word-count-min">${min}</span>/`);
    }

    pieces.push(`<span class="comment--item-word-count-user">${wordCount}</span>`);

    if (max) {
        pieces.push(`/<span class="comment--item-word-count-max">${max}</span>`);
    }

    return pieces.join("");
}

function getWordLimitPlugins(settings) {
    if (!("wordsLimit" in settings)) {
        return [];
    }

    const {
        wordsLimit: { max: maxWords, min: minWords },
    } = settings;

    const maxWordsLimit = maxWords ? parseInt(maxWords, 10) : 0;
    const minWordsLimit = minWords ? parseInt(minWords, 10) : 0;

    if (!maxWordsLimit && !minWordsLimit) {
        return [];
    }

    function view(editorView) {
        const wordCountHolder = document.createElement("div");
        wordCountHolder.classList.add("comment--item-word-count");

        wordCountHolder.innerHTML = getWordCountText(0, minWordsLimit, maxWordsLimit);
        editorView.dom.parentNode.appendChild(wordCountHolder);

        function update(view, lastState) {
            const state = view.state;
            if (lastState && lastState.doc.eq(state.doc)) {
                return;
            }

            const currentWords = getWordCount(state.doc.textContent);
            wordCountHolder.innerHTML = getWordCountText(
                currentWords,
                minWordsLimit,
                maxWordsLimit
            );
        }

        function destroy() {
            wordCountHolder.remove();
        }

        return { update, destroy };
    }

    function filterTransaction(transaction) {
        if (!transaction.docChanged || !maxWordsLimit) {
            return true;
        }

        return getWordCount(transaction.doc.textContent) <= maxWordsLimit;
    }

    const wordLimitPlugin = new Plugin({
        view,
        filterTransaction,
    });
    return [wordLimitPlugin];
}

function getLinkPlugins(settings, schema) {
    if (!settings.enableLinks) {
        return [];
    }

    const markType = schema.marks.link;

    return [keymap({ "Mod-q": addLink(markType) })];
}

export default function getPlugins(schema, settings) {
    const toggleStrong = toggleMark(schema.marks.strong);
    const toggleEmphasis = toggleMark(schema.marks.em);
    const addHardBreak = chainCommands(exitCode, (state, dispatch) => {
        dispatch(state.tr.replaceSelectionWith(schema.nodes.hard_break.create()).scrollIntoView());
        return true;
    });

    const plugins = [
        keymap({
            "Mod-z": undo,
            "Shift-Mod-z": redo,
            "Mod-y": redo,
            "Mod-b": toggleStrong,
            "Mod-B": toggleStrong,
            "Mod-i": toggleEmphasis,
            "Mod-I": toggleEmphasis,
            "Mod-Enter": addHardBreak,
            "Shift-Enter": addHardBreak,
            "Ctrl-Enter": addHardBreak,
        }),
        keymap(baseKeymap),
        getMenu(settings, schema),
        history(),
        ...getLinkPlugins(settings, schema),
        ...getWordLimitPlugins(settings),
    ];
    return plugins;
}
